import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Alert, Box, Button, Divider, Paper, Stack, Typography } from '@mui/material';
import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { useMemo, useState } from 'react';
import { formatBoxPieces } from '@tiles-erp/shared';
import { usePagination } from '@tiles-erp/hooks';
import { PageContainer } from '@tiles-erp/ui';
import type { GodownStockMatrixColumn, GodownStockMatrixRow } from '@tiles-erp/shared-types';
import { DataTable } from '../../components/DataTable';
import { SearchableSelect } from '../../components/SearchableSelect';
import { downloadTableExcel, downloadTablePdf, fetchAllListRows, type ExportColumn } from '../../components/ListExportButtons';
import { useCatalogOptions } from '../catalog/api';
import { useProductSizes } from '../products/api';
import { useGodownStockMatrix } from './api';

const quantityLabel = (row: GodownStockMatrixRow, godownId: string): string => {
  const quantity = row.quantities[godownId] ?? 0;
  if (!quantity) return '';
  return formatBoxPieces(quantity, row.piecesPerBox, row.baseUom === 'PIECE' || row.piecesPerBox === 1);
};

const dynamicExportColumns = (columns: GodownStockMatrixColumn[]): ExportColumn<GodownStockMatrixRow>[] => [
  { header: 'SKU', value: (row) => row.sku, width: 85 },
  { header: 'Product name', value: (row) => row.productName, width: 190 },
  { header: 'Brand', value: (row) => row.brandName, width: 100 },
  { header: 'Category', value: (row) => row.categoryName, width: 120 },
  { header: 'Size', value: (row) => row.sizeMm ?? '', width: 65 },
  ...columns.map((column) => ({
    header: `${column.branchName} - ${column.godownName}`,
    value: (row: GodownStockMatrixRow) => quantityLabel(row, column.godownId),
    width: 105,
  })),
];

/** One product per row, with every accessible branch and godown across the columns. */
export function AllGodownStockReportPage(): JSX.Element {
  const pagination = usePagination({ initialPageSize: 100 });
  const [brandId, setBrandId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [sizeMm, setSizeMm] = useState('');
  const [exporting, setExporting] = useState<'excel' | 'pdf' | null>(null);
  const [exportError, setExportError] = useState('');
  const brands = useCatalogOptions('/brands');
  const categories = useCatalogOptions('/categories', brandId || undefined);
  const sizes = useProductSizes();
  const filters = { brandId: brandId || undefined, categoryId: categoryId || undefined, sizeMm: sizeMm || undefined };
  const report = useGodownStockMatrix(pagination.query, filters);

  const columns = useMemo<Array<ColDef<GodownStockMatrixRow> | ColGroupDef<GodownStockMatrixRow>>>(() => {
    const fixed: ColDef<GodownStockMatrixRow>[] = [
      { field: 'sku', headerName: 'SKU', minWidth: 120, pinned: 'left' },
      { field: 'productName', headerName: 'Product name', minWidth: 220, pinned: 'left' },
      { field: 'brandName', headerName: 'Brand', minWidth: 130 },
      { field: 'categoryName', headerName: 'Category', minWidth: 150 },
      { field: 'sizeMm', headerName: 'Size', minWidth: 100 },
    ];
    const branches = new Map<string, { name: string; godowns: GodownStockMatrixColumn[] }>();
    for (const column of report.data?.columns ?? []) {
      const branch = branches.get(column.branchId) ?? { name: column.branchName, godowns: [] };
      branch.godowns.push(column);
      branches.set(column.branchId, branch);
    }
    return [
      ...fixed,
      ...[...branches.values()].map<ColGroupDef<GodownStockMatrixRow>>((branch) => ({
        headerName: branch.name,
        marryChildren: true,
        children: branch.godowns.map((godown) => ({
          headerName: godown.godownName,
          minWidth: 125,
          valueGetter: (params) => params.data ? quantityLabel(params.data, godown.godownId) : '',
          cellStyle: { fontWeight: 600, textAlign: 'right' },
        })),
      })),
    ];
  }, [report.data?.columns]);

  const runExport = async (format: 'excel' | 'pdf'): Promise<void> => {
    setExportError('');
    setExporting(format);
    try {
      const rows = await fetchAllListRows<GodownStockMatrixRow>('/stock/godown-matrix', {
        ...filters,
        search: pagination.query.search,
      });
      if (!rows.length) throw new Error('There is no stock to export for these filters.');
      const exportColumns = dynamicExportColumns(report.data?.columns ?? []);
      const filename = `all-branch-godown-stock-${new Date().toISOString().slice(0, 10)}`;
      if (format === 'excel') downloadTableExcel(`${filename}.xls`, 'All branch and godown stock', exportColumns, rows);
      else downloadTablePdf(`${filename}.pdf`, 'All branch and godown stock', exportColumns, rows);
    } catch (error) {
      setExportError(error instanceof Error ? error.message : 'The report could not be exported.');
    } finally {
      setExporting(null);
    }
  };

  return (
    <PageContainer
      title="All branch and godown stock report"
      subtitle="Each product appears once. Branches are grouped across the top with their godowns below."
      actions={<Stack direction="row" spacing={1}>
        <Button variant="outlined" startIcon={<DownloadIcon />} disabled={Boolean(exporting) || !report.data?.items.length}
          onClick={() => void runExport('excel')}>{exporting === 'excel' ? 'Exporting…' : 'Excel'}</Button>
        <Button variant="outlined" startIcon={<PictureAsPdfIcon />} disabled={Boolean(exporting) || !report.data?.items.length}
          onClick={() => void runExport('pdf')}>{exporting === 'pdf' ? 'Exporting…' : 'PDF'}</Button>
      </Stack>}
    >
      <Stack spacing={1.5}>
        {exportError && <Alert severity="error" onClose={() => setExportError('')}>{exportError}</Alert>}
        {report.error && <Alert severity="error">{report.error.message}</Alert>}
        <Paper variant="outlined" sx={{ p: { xs: 1.25, sm: 1.5 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <SearchableSelect label="Brand" value={brandId} allLabel="All brands"
              options={(brands.data ?? []).map((brand) => ({ value: brand.id, label: brand.name }))}
              onChange={(nextBrandId) => {
                setBrandId(nextBrandId);
                setCategoryId('');
                pagination.setPage(1);
              }} width={{ xs: '100%', sm: 220 }} />
            <SearchableSelect label="Category" value={categoryId} allLabel="All categories"
              options={(categories.data ?? []).map((category) => ({ value: category.id, label: category.name }))}
              onChange={(nextCategoryId) => { setCategoryId(nextCategoryId); pagination.setPage(1); }}
              width={{ xs: '100%', sm: 240 }} />
            <SearchableSelect label="Size" value={sizeMm} allLabel="All sizes"
              options={(sizes.data ?? []).map((size) => ({ value: size, label: size }))}
              onChange={(nextSize) => { setSizeMm(nextSize); pagination.setPage(1); }}
              width={{ xs: '100%', sm: 180 }} />
          </Stack>
        </Paper>
        <Typography variant="body2" color="text.secondary">
          {report.data ? `${report.data.meta.totalItems.toLocaleString('en-IN')} products · ${report.data.columns.length} godowns` : 'Loading stock…'}
        </Typography>
        <DataTable rows={report.data?.items ?? []} columns={columns} meta={report.data?.meta}
          pagination={pagination} loading={report.isLoading || report.isFetching}
          searchPlaceholder="Search by SKU or product…" exportable={false} height={650}
          gridOptions={{ groupHeaderHeight: 34 }}
          mobileRowRenderer={(row) => {
            const stocked = (report.data?.columns ?? [])
              .map((column) => ({ column, label: quantityLabel(row, column.godownId) }))
              .filter((entry) => entry.label);
            return (
              <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                <Box sx={{ p: 1.5 }}>
                  <Typography fontWeight={700}>{row.sku} · {row.productName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {[row.brandName, row.categoryName, row.sizeMm].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
                <Divider />
                {stocked.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
                    No stock in accessible godowns
                  </Typography>
                ) : (
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto' }}>
                    {stocked.map(({ column, label }) => (
                      <Box key={column.godownId} sx={{ display: 'contents' }}>
                        <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {column.branchName}
                          </Typography>
                          <Typography variant="body2">{column.godownName}</Typography>
                        </Box>
                        <Typography sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider', fontWeight: 700, textAlign: 'right' }}>
                          {label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            );
          }} />
      </Stack>
    </PageContainer>
  );
}
